import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../config/axiosConfig";
import { useSocket } from "../contexts/SocketContext";

const PaymentForm = ({
  planId,
  onClose,
  darkMode,
  subscribing,
  setSubscribing,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const { socket } = useSocket();
  const [isProcessing, setIsProcessing] = useState(false);

  // Socket.IO
  useEffect(() => {
    if (!socket) return;

    socket.on("subscriptionUpdated", (data) => {
      console.log("Événement subscriptionUpdated reçu:", data);
      if (data.status === "active" && data.invoiceStatus === "paid") {
        toast.success(`Abonnement ${data.subscriptionPlan} activé ! ID: ${data.subscriptionId}`);
        setSubscriptionData(null);
        setSubscribing(false);
        redirectUser();
        onClose();
      } else if (data.status === "incomplete") {
        setSubscriptionData({
          subscriptionId: data.subscriptionId,
          subscriptionStatus: data.status,
          plan: data.subscriptionPlan,
          invoiceId: data.invoiceId || null,
        });
        setCardError("Paiement en attente. Veuillez vérifier via votre banque.");
        toast.warning("Souscription en attente de validation", {
          position: "top-center",
          autoClose: 5000,
        });
      }
    });

    socket.on("connect_error", (error) => {
      console.error("Erreur Socket.IO:", error);
      toast.error("Erreur de connexion au serveur de notifications");
    });

    return () => {
      socket.off("subscriptionUpdated");
      socket.off("connect_error");
    };
  }, [socket, onClose, setSubscribing]);

  // Vérification initiale
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await api.get("/users/me");
        setRole(response.data.role);
        console.log("Rôle récupéré:", response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération du rôle:", error);
        toast.error("Erreur lors de la récupération du rôle");
      }
    };

    const checkSubscriptionStatus = async () => {
      try {
        const response = await api.get("/subscription/status");
        console.log("Statut de la souscription:", response.data);
        const { subscription, subscriptionPlan } = response.data;
        if (subscription && subscription.status === "incomplete") {
          const invoiceResponse = await api.get(`/subscription/invoice/${subscription.id}`);
          console.log("Invoice initiale:", invoiceResponse.data);
          if (invoiceResponse.data.status === "paid") {
            // Facture payée, forcer la synchronisation
            console.log("Facture payée, synchronisation avec Stripe...");
            const syncResponse = await api.post("/subscription/sync", {
              subscriptionId: subscription.id,
            });
            console.log("Réponse synchronisation:", syncResponse.data);
            if (syncResponse.data.status === "active") {
              toast.success(`Abonnement ${subscriptionPlan} activé ! ID: ${subscription.id}`);
              setSubscribing(false);
              redirectUser();
              onClose();
              return;
            }
          }
          setSubscriptionData({
            subscriptionId: subscription.id,
            subscriptionStatus: subscription.status,
            plan: subscriptionPlan,
            invoiceId: invoiceResponse.data.id || null,
          });
          toast.warning(`Vous avez une souscription ${subscriptionPlan} en attente. Veuillez la finaliser.`, {
            position: "top-center",
            autoClose: 5000,
          });
        } else if (subscription && subscription.status === "active") {
          toast.success(`Vous avez déjà un abonnement ${subscriptionPlan} actif`);
          setSubscribing(false);
          redirectUser();
          onClose();
        }
      } catch (error) {
        console.error("Erreur lors de la vérification de la souscription:", error);
        toast.error("Erreur lors de la vérification de l'abonnement");
      }
    };

    fetchUserRole();
    if (planId) {
      checkSubscriptionStatus();
    }
  }, [planId, onClose, setSubscribing]);

  // Polling amélioré
  const pollSubscriptionStatus = useCallback(
    async (subscriptionId, maxAttempts = 20, delay = 1500) => {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          console.log(`Polling statut souscription, tentative ${attempt}/${maxAttempts}`);
          const response = await api.get("/subscription/status");
          console.log("Réponse polling:", response.data);
          if (
            response.data.subscription?.status === "active" ||
            response.data.subscription?.latest_invoice_status === "paid"
          ) {
            toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
            setSubscribing(false);
            redirectUser();
            onClose();
            return true;
          }
          // Vérifier la facture si l'abonnement est toujours incomplet
          const invoiceResponse = await api.get(`/subscription/invoice/${subscriptionId}`);
          console.log("Invoice polling:", invoiceResponse.data);
          if (invoiceResponse.data.status === "paid") {
            console.log("Facture payée, synchronisation avec Stripe...");
            const syncResponse = await api.post("/subscription/sync", {
              subscriptionId,
            });
            console.log("Réponse synchronisation:", syncResponse.data);
            if (syncResponse.data.status === "active") {
              toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
              setSubscribing(false);
              redirectUser();
              onClose();
              return true;
            }
          }
          await new Promise((resolve) => setTimeout(resolve, delay));
        } catch (error) {
          console.error("Erreur polling statut souscription:", error);
        }
      }
      console.warn("Échec du polling après toutes les tentatives");
      toast.error("Échec de la vérification du statut après plusieurs tentatives");
      return false;
    },
    [planId, onClose, setSubscribing]
  );

  // Redirection
  const redirectUser = useCallback(() => {
    if (!role) {
      console.warn("Rôle non défini → /home-page");
      navigate("/home-page");
      return;
    }

    switch (role) {
      case "particulier":
      case "learner":
        navigate("/dashboard");
        break;

      case "entreprise":
      case "collectivité":
      case "établissement":
      case "admin":
        navigate("/admin");
        break;

      default:
        console.warn(`Rôle non géré : ${role} → fallback /home-page`);
        navigate("/home-page");
    }
  }, [role, navigate]);

  // Gestion de la soumission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || subscribing || isProcessing) {
      setCardError("Stripe non chargé ou traitement en cours");
      toast.error("Impossible de traiter le paiement");
      return;
    }

    if (!["academia", "pro", "entreprise"].includes(planId)) {
      setCardError("Plan sélectionné invalide");
      toast.error("Plan invalide");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement || !cardElement._complete) {
      setCardError("Veuillez saisir des informations de carte valides");
      toast.error("Coordonnées bancaires invalides");
      return;
    }

    setIsProcessing(true);
    setSubscribing(true);

    try {
      // Gestion d'une souscription existante
      if (subscriptionData && subscriptionData.plan === planId) {
        const { subscriptionId, invoiceId } = subscriptionData;

        if (!invoiceId) {
          console.error("ID de facture manquant dans subscriptionData");
          setCardError("ID de facture manquant. Veuillez réessayer.");
          toast.error("Erreur : ID de facture manquant");
          return;
        }

        // Vérifier l'état de la facture
        console.log("Vérification facture existante:", invoiceId);
        const invoiceResponse = await api.get(`/subscription/invoice/${subscriptionId}`);
        console.log("État de la facture:", invoiceResponse.data);

        if (invoiceResponse.data.status === "paid") {
          console.log("Facture payée, synchronisation avec Stripe...");
          const syncResponse = await api.post("/subscription/sync", {
            subscriptionId,
          });
          console.log("Réponse synchronisation:", syncResponse.data);
          if (syncResponse.data.status === "active") {
            toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
            setSubscribing(false);
            redirectUser();
            onClose();
            return;
          }
        }

        // Vérifier ou créer un payment_intent
        if (invoiceResponse.data.payment_intent) {
          const paymentIntentResponse = await stripe.retrievePaymentIntent(
            invoiceResponse.data.payment_intent.client_secret
          );
          console.log("PaymentIntent récupéré:", paymentIntentResponse);

          if (paymentIntentResponse.status === "requires_action") {
            console.log("Tentative de confirmation 3D Secure pour:", paymentIntentResponse.id);
            const { error, paymentIntent: confirmedIntent } = await stripe.confirmCardPayment(
              invoiceResponse.data.payment_intent.client_secret,
              {
                payment_method: { card: cardElement },
                setup_future_usage: "off_session",
              }
            );
            if (error) {
              console.error("Erreur 3D Secure:", error);
              throw new Error(error.message);
            }
            console.log("3D Secure confirmé:", confirmedIntent.id, confirmedIntent.status);

            // Vérifier immédiatement le statut de l'abonnement
            const statusResponse = await api.get("/subscription/status");
            console.log("Statut après confirmation 3DS:", statusResponse.data);
            if (statusResponse.data.subscription?.status === "active" || statusResponse.data.subscription?.latest_invoice_status === "paid") {
              toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
              setSubscribing(false);
              redirectUser();
              onClose();
              return;
            }

            // Tenter de finaliser la facture
            console.log("Tentative de finalisation de la facture:", invoiceId);
            try {
              const finalizeResponse = await api.post(`/subscription/finalize-invoice/${invoiceId}`);
              console.log("Réponse finalisation:", finalizeResponse.data);
              if (finalizeResponse.data.status === "paid") {
                const statusResponse = await api.get("/subscription/status");
                console.log("Statut après finalisation:", statusResponse.data);
                if (statusResponse.data.subscription?.status === "active") {
                  toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                  setSubscribing(false);
                  redirectUser();
                  onClose();
                  return;
                }
              }
            } catch (error) {
              console.error("Erreur lors de la finalisation:", error);
              if (error.response?.status === 500) {
                console.log("Erreur 500, tentative de synchronisation...");
                const syncResponse = await api.post("/subscription/sync", {
                  subscriptionId,
                });
                console.log("Réponse synchronisation:", syncResponse.data);
                if (syncResponse.data.status === "active") {
                  toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                  setSubscribing(false);
                  redirectUser();
                  onClose();
                  return;
                }
              }
              throw error;
            }

            // Si toujours pas activé, attendre via polling
            if (await pollSubscriptionStatus(subscriptionId)) {
              return;
            }
          } else if (paymentIntentResponse.status === "succeeded") {
            console.log("PaymentIntent déjà réussi:", paymentIntentResponse.id);
            try {
              const finalizeResponse = await api.post(`/subscription/finalize-invoice/${invoiceId}`);
              console.log("Réponse finalisation (succeeded):", finalizeResponse.data);
              if (finalizeResponse.data.status === "paid") {
                const statusResponse = await api.get("/subscription/status");
                console.log("Statut après finalisation (succeeded):", statusResponse.data);
                if (statusResponse.data.subscription?.status === "active") {
                  toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                  setSubscribing(false);
                  redirectUser();
                  onClose();
                  return;
                }
              }
            } catch (error) {
              console.error("Erreur lors de la finalisation (succeeded):", error);
              if (error.response?.status === 500) {
                console.log("Erreur 500, tentative de synchronisation...");
                const syncResponse = await api.post("/subscription/sync", {
                  subscriptionId,
                });
                console.log("Réponse synchronisation:", syncResponse.data);
                if (syncResponse.data.status === "active") {
                  toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                  setSubscribing(false);
                  redirectUser();
                  onClose();
                  return;
                }
              }
              throw error;
            }
          } else {
            console.warn("PaymentIntent inattendu:", paymentIntentResponse.status);
            throw new Error(`Statut du paiement inattendu: ${paymentIntentResponse.status}`);
          }
        } else if (invoiceResponse.data.amount_due > 0) {
          console.log("Aucun payment_intent, création d'un nouveau pour facture:", invoiceId);
          const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });
          if (pmError) {
            console.error("Erreur création payment_method:", pmError);
            throw new Error(pmError.message);
          }

          const payResponse = await api.post(`/subscription/pay-invoice/${invoiceId}`, {
            paymentMethodId: paymentMethod.id,
          });
          console.log("Réponse pay-invoice:", payResponse.data);

          if (payResponse.data.clientSecret) {
            const { error, paymentIntent } = await stripe.confirmCardPayment(payResponse.data.clientSecret, {
              payment_method: paymentMethod.id,
              setup_future_usage: "off_session",
            });
            if (error) {
              console.error("Erreur confirmation nouveau payment_intent:", error);
              throw new Error(error.message);
            }
            console.log("Nouveau payment_intent confirmé:", paymentIntent.id, paymentIntent.status);

            if (paymentIntent.status === "succeeded" || paymentIntent.status === "requires_action") {
              const statusResponse = await api.get("/subscription/status");
              console.log("Statut après nouveau PI:", statusResponse.data);
              if (statusResponse.data.subscription?.status === "active" || statusResponse.data.subscription?.latest_invoice_status === "paid") {
                toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                setSubscribing(false);
                redirectUser();
                onClose();
                return;
              }

              if (paymentIntent.status === "succeeded") {
                console.log("Finalisation facture pour nouveau succeeded:", invoiceId);
                try {
                  const finalizeResponse = await api.post(`/subscription/finalize-invoice/${invoiceId}`);
                  console.log("Réponse finalisation (nouveau PI):", finalizeResponse.data);
                  if (finalizeResponse.data.status === "paid") {
                    const statusResponse = await api.get("/subscription/status");
                    console.log("Statut après finalisation (nouveau PI):", statusResponse.data);
                    if (statusResponse.data.subscription?.status === "active") {
                      toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                      setSubscribing(false);
                      redirectUser();
                      onClose();
                      return;
                    }
                  }
                } catch (error) {
                  console.error("Erreur lors de la finalisation (nouveau PI):", error);
                  if (error.response?.status === 500) {
                    console.log("Erreur 500, tentative de synchronisation...");
                    const syncResponse = await api.post("/subscription/sync", {
                      subscriptionId,
                    });
                    console.log("Réponse synchronisation:", syncResponse.data);
                    if (syncResponse.data.status === "active") {
                      toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                      setSubscribing(false);
                      redirectUser();
                      onClose();
                      return;
                    }
                  }
                  throw error;
                }
              }

              setSubscriptionData({
                subscriptionId,
                subscriptionStatus: "incomplete",
                plan: planId,
                invoiceId,
              });
              setCardError("Paiement en attente. Veuillez vérifier avec votre banque.");
              toast.warning("Souscription en attente de validation");
              await pollSubscriptionStatus(subscriptionId);
              return;
            }
          } else {
            throw new Error("Impossible de créer un nouveau paiement pour la facture");
          }
        } else {
          console.log("Facture avec amount_due=0, tentative de finalisation:", invoiceId);
          try {
            const finalizeResponse = await api.post(`/subscription/finalize-invoice/${invoiceId}`);
            console.log("Réponse finalisation (amount_due=0):", finalizeResponse.data);
            if (finalizeResponse.data.status === "paid") {
              const statusResponse = await api.get("/subscription/status");
              console.log("Statut après finalisation (amount_due=0):", statusResponse.data);
              if (statusResponse.data.subscription?.status === "active") {
                toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                setSubscribing(false);
                redirectUser();
                onClose();
                return;
              }
            }
          } catch (error) {
            console.error("Erreur lors de la finalisation (amount_due=0):", error);
            if (error.response?.status === 500) {
              console.log("Erreur 500, tentative de synchronisation...");
              const syncResponse = await api.post("/subscription/sync", {
                subscriptionId,
              });
              console.log("Réponse synchronisation:", syncResponse.data);
              if (syncResponse.data.status === "active") {
                toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                setSubscribing(false);
                redirectUser();
                onClose();
                return;
              }
            }
            throw error;
          }
          throw new Error("Facture sans paiement requis mais non finalisée");
        }

        setCardError("Paiement en attente. Veuillez vérifier avec votre banque.");
        toast.warning("Paiement en attente de validation");
        return;
      }

      // Nouvelle souscription
      console.log("Création d'une nouvelle souscription pour plan:", planId);
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });
      if (pmError) {
        console.error("Erreur création payment_method:", pmError);
        throw new Error(pmError.message);
      }

      const response = await api.post("/subscription/subscribe", {
        plan: planId,
        paymentMethodId: paymentMethod.id,
      });
      console.log("Réponse souscription:", response.data);

      const { clientSecret, subscriptionId, intentType, subscriptionStatus, paymentIntentId, latestInvoiceId } = response.data;

      if (!clientSecret) {
        console.log("Aucun clientSecret, souscription en attente:", subscriptionId);
        setSubscriptionData({
          subscriptionId,
          subscriptionStatus,
          plan: planId,
          invoiceId: latestInvoiceId || null,
        });
        if (await pollSubscriptionStatus(subscriptionId)) {
          return;
        }
        setCardError("Paiement en attente. Veuillez vérifier avec votre banque.");
        toast.warning("Souscription en attente de validation");
        return;
      }

      if (intentType === "payment_intent") {
        console.log("Confirmation payment_intent:", paymentIntentId);
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethod.id,
          setup_future_usage: "off_session",
        });
        if (error) {
          console.error("Erreur confirmation payment_intent:", error);
          throw new Error(error.message);
        }
        console.log("Payment intent confirmé:", paymentIntent.id, paymentIntent.status);

        let invoiceId = latestInvoiceId;
        if (!invoiceId) {
          try {
            const invoiceResponse = await api.get(`/subscription/invoice/${subscriptionId}`);
            invoiceId = invoiceResponse.data.id;
            console.log("Invoice ID récupéré:", invoiceId);
          } catch (error) {
            console.error("Erreur lors de la récupération de l'invoice:", error);
            invoiceId = null;
          }
        }

        if (paymentIntent.status === "succeeded" || paymentIntent.status === "requires_action") {
          console.log("Vérification statut après confirmation, paymentIntent status:", paymentIntent.status);
          if (await pollSubscriptionStatus(subscriptionId)) {
            return;
          }

          if (invoiceId && paymentIntent.status === "succeeded") {
            console.log("Finalisation facture pour succeeded:", invoiceId);
            try {
              const finalizeResponse = await api.post(`/subscription/finalize-invoice/${invoiceId}`);
              console.log("Réponse finalisation (new):", finalizeResponse.data);
              if (finalizeResponse.data.status === "paid") {
                const statusResponse = await api.get("/subscription/status");
                console.log("Statut après finalisation (new):", statusResponse.data);
                if (statusResponse.data.subscription?.status === "active") {
                  toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                  setSubscribing(false);
                  redirectUser();
                  onClose();
                  return;
                }
              }
            } catch (error) {
              console.error("Erreur lors de la finalisation (new):", error);
              if (error.response?.status === 500) {
                console.log("Erreur 500, tentative de synchronisation...");
                const syncResponse = await api.post("/subscription/sync", {
                  subscriptionId,
                });
                console.log("Réponse synchronisation:", syncResponse.data);
                if (syncResponse.data.status === "active") {
                  toast.success(`Abonnement ${planId} activé ! ID: ${subscriptionId}`);
                  setSubscribing(false);
                  redirectUser();
                  onClose();
                  return;
                }
              }
              throw error;
            }
          }

          console.log("Définition subscriptionData pour attente:", { subscriptionId, invoiceId });
          setSubscriptionData({
            subscriptionId,
            subscriptionStatus: "incomplete",
            plan: planId,
            invoiceId,
          });
          setCardError("Paiement en attente. Veuillez vérifier avec votre banque.");
          toast.warning("Souscription en attente de validation");
          await pollSubscriptionStatus(subscriptionId);
        }
      } else if (intentType === "setup_intent") {
        console.log("Confirmation setup_intent:", paymentIntentId);
        const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: paymentMethod.id,
        });
        if (error) {
          console.error("Erreur confirmation setup_intent:", error);
          throw new Error(error.message);
        }
        console.log("Setup intent confirmé:", setupIntent.id, setupIntent.status);

        let invoiceId = latestInvoiceId;
        if (!invoiceId) {
          try {
            const invoiceResponse = await api.get(`/subscription/invoice/${subscriptionId}`);
            invoiceId = invoiceResponse.data.id;
            console.log("Invoice ID récupéré (setup):", invoiceId);
          } catch (error) {
            console.error("Erreur lors de la récupération de l'invoice (setup):", error);
            invoiceId = null;
          }
        }

        if (await pollSubscriptionStatus(subscriptionId)) {
          return;
        }

        console.log("Définition subscriptionData pour attente (setup):", { subscriptionId, invoiceId });
        setSubscriptionData({
          subscriptionId,
          subscriptionStatus,
          plan: planId,
          invoiceId,
        });
        setCardError("Paiement en attente. Veuillez vérifier avec votre banque.");
        toast.warning("Souscription en attente de validation");
      } else {
        console.error("Type d’intention invalide:", intentType);
        throw new Error("Type d’intention invalide");
      }
    } catch (error) {
      console.error("Erreur lors du paiement:", error);
      setCardError(error.message || "Erreur lors du paiement");
      toast.error(error.message || "Erreur lors du paiement");
    } finally {
      setIsProcessing(false);
      setSubscribing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: "auto", p: 0 }}>
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 4, borderLeft: "2px solid #f59e0b", pl: 2 }}>
          <Typography
            variant="h6"
            sx={{ 
              fontSize: "1rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: "#fff",
              fontFamily: "'Inter', sans-serif"
            }}
          >
            {subscriptionData
              ? `Confirmation_Protocole_${planId}`
              : `Initialisation_Paiement_${planId}`}
          </Typography>
          <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.4)", fontMono: "true" }}>
            STATUS: ENCRYPTED_SESSION_v2.4
          </Typography>
        </Box>

        {/* Container Stripe - Épuré et net */}
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            backgroundColor: "rgba(0,0,0,0.4)",
            border: "1px solid",
            borderColor: "rgba(245, 158, 11, 0.2)",
            borderRadius: "12px",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: "rgba(245, 158, 11, 0.4)",
              backgroundColor: "rgba(0,0,0,0.6)",
            }
          }}
        >
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "15px",
                  color: "#fff",
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: "0.05em",
                  "::placeholder": { color: "rgba(255,255,255,0.3)" },
                },
                invalid: { 
                  color: "#ef4444",
                  iconColor: "#ef4444" 
                },
              },
            }}
            onChange={(e) => setCardError(e.error ? e.error.message : null)}
          />
        </Box>

        {/* Messages d'erreur ou d'info - Sobres */}
        {cardError && (
          <Typography sx={{ mb: 2, fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>
            {`> ERROR: ${cardError}`}
          </Typography>
        )}

        {subscriptionData && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: "rgba(34, 211, 238, 0.05)", 
            border: "1px dashed rgba(34, 211, 238, 0.2)",
            borderRadius: "8px"
          }}>
            <Typography sx={{ fontSize: "11px", color: "rgba(34, 211, 238, 0.8)", lineHeight: 1.5 }}>
              {`SOUSCRIPTION EN ATTENTE: ID_${subscriptionData.subscriptionId.slice(-8)}`}
              <br />
              {subscriptionData.invoiceId
                ? `Veuillez finaliser la facture #${subscriptionData.invoiceId.slice(-6)}`
                : "Veuillez vérifier l'autorisation bancaire."}
            </Typography>
          </Box>
        )}

        {/* Actions - Style "Control Panel" */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Button
            type="submit"
            disabled={subscribing || isProcessing || !stripe}
            fullWidth
            sx={{
              py: 1.5,
              bgcolor: "#f59e0b",
              color: "#000",
              fontWeight: 900,
              fontSize: "12px",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              borderRadius: "8px",
              "&:hover": { 
                bgcolor: "#d97706",
                boxShadow: "0 0 20px rgba(245, 158, 11, 0.3)" 
              },
              "&.Mui-disabled": { bgcolor: "rgba(245, 158, 11, 0.2)", color: "rgba(0,0,0,0.5)" }
            }}
          >
            {isProcessing ? (
              <CircularProgress size={18} sx={{ color: "#000" }} />
            ) : subscriptionData ? (
              "Finaliser"
            ) : (
              "Autoriser le Paiement"
            )}
          </Button>

          <Button
            onClick={onClose}
            fullWidth
            disabled={isProcessing}
            sx={{
              py: 1,
              color: "rgba(255,255,255,0.4)",
              fontSize: "10px",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              "&:hover": { color: "#fff", bgcolor: "transparent" }
            }}
          >
            Annuler l'opération
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PaymentForm;